

/** Introspection for schema explorer — field types, args, and input object fields. */
export const GQL_INTROSPECTION_QUERY = `
query IntrospectionQuery {
  __schema {
    queryType { name }
    mutationType { name }
    subscriptionType { name }
    types {
      kind
      name
      enumValues(includeDeprecated: true) {
        name
        description
      }
      fields(includeDeprecated: true) {
        name
        description
        args {
          name
          description
          type { kind name ofType { kind name ofType { kind name ofType { kind name } } } }
        }
        type { kind name ofType { kind name ofType { kind name ofType { kind name } } } }
      }
      inputFields {
        name
        description
        type { kind name ofType { kind name ofType { kind name ofType { kind name } } } }
      }
    }
  }
}`.trim();

/** Fallback when the primary introspection query is rejected (depth/complexity limits). */
export const GQL_INTROSPECTION_QUERY_MINIMAL = `
query IntrospectionQuery {
  __schema {
    queryType { name }
    types {
      kind
      name
      enumValues(includeDeprecated: true) {
        name
        description
      }
      fields(includeDeprecated: true) {
        name
        description
        args {
          name
          description
          type { kind name ofType { kind name ofType { kind name } } }
        }
        type { kind name ofType { kind name ofType { kind name ofType { kind name } } } }
      }
      inputFields {
        name
        description
        type { kind name ofType { kind name ofType { kind name } } }
      }
    }
  }
}`.trim();

export const GQL_INTROSPECTION_QUERIES = [
  GQL_INTROSPECTION_QUERY,
  GQL_INTROSPECTION_QUERY_MINIMAL,
];

/** Unwrap NON_NULL / LIST wrappers to the named type. */
export function unwrapGraphQLType(type) {
  if (!type) return null;
  if (type.kind === 'NON_NULL' || type.kind === 'LIST') {
    return unwrapGraphQLType(type.ofType);
  }
  return type;
}

/** Format a GraphQL type reference like Postman: `[Country!]!`, `String`, `ID!`. */
export function formatGraphQLType(type) {
  if (!type) return '';
  if (type.kind === 'NON_NULL') {
    const inner = formatGraphQLType(type.ofType);
    return inner.endsWith('!') ? inner : `${inner}!`;
  }
  if (type.kind === 'LIST') {
    return `[${formatGraphQLType(type.ofType)}]`;
  }
  if (type.name) return type.name;
  return type.kind || '';
}

export function getTypeClassName(type) {
  if (!type) return '';
  const inner = unwrapGraphQLType(type);
  if (!inner?.kind) return '';
  if (inner.kind === 'SCALAR' || inner.kind === 'ENUM') return 'gql-schema-type-hint--scalar';
  if (inner.kind === 'INPUT_OBJECT') return 'gql-schema-type-hint--input';
  if (inner.kind === 'OBJECT' || inner.kind === 'INTERFACE' || inner.kind === 'UNION') {
    return 'gql-schema-type-hint--object';
  }
  return '';
}

export function isScalarKind(kind) {
  return kind === 'SCALAR' || kind === 'ENUM';
}

export function isCompositeKind(kind) {
  return kind === 'OBJECT' || kind === 'INTERFACE' || kind === 'UNION';
}

export function fieldOutputType(type) {
  return unwrapGraphQLType(type);
}

/** Build a name → type definition map from introspection schema. */
export function buildSchemaIndex(schema) {
  const index = new Map();
  if (!schema?.types) return index;
  for (const type of schema.types) {
    if (type?.name) index.set(type.name, type);
  }
  return index;
}

/** Root query fields for the schema explorer (excludes __typename etc.). */
export function getQueryRootFields(schema, schemaIndex) {
  const queryTypeName = schema?.queryType?.name;
  if (!queryTypeName) return [];
  const queryType = schemaIndex.get(queryTypeName);
  if (!queryType?.fields) return [];
  return queryType.fields.filter((f) => f?.name && !f.name.startsWith('__'));
}

/** Subfields for a field node in the explorer tree. */
export function getExplorerChildFields(field, schemaIndex) {
  const output = fieldOutputType(field?.type);
  if (!output?.name) return [];
  const typeDef = schemaIndex.get(output.name);
  if (!typeDef?.fields || !isCompositeKind(typeDef.kind)) return [];
  return typeDef.fields.filter((f) => f?.name && !f.name.startsWith('__'));
}

export function isCompositeField(field, schemaIndex) {
  const output = fieldOutputType(field?.type);
  if (!output?.name) return false;
  const typeDef = schemaIndex.get(output.name);
  return !!typeDef && isCompositeKind(typeDef.kind) && (typeDef.fields?.length ?? 0) > 0;
}

export function getFieldOutputTypeName(field) {
  return fieldOutputType(field?.type)?.name ?? null;
}

/** True when expanding this field would revisit an object type already in the path. */
export function isCyclicField(field, visitedTypes) {
  const typeName = getFieldOutputTypeName(field);
  return !!(typeName && visitedTypes?.has(typeName));
}

export function isExpandableField(field, schemaIndex, visitedTypes) {
  return isCompositeField(field, schemaIndex) && !isCyclicField(field, visitedTypes);
}

/** Args declared on a field (for schema explorer). */
export function getFieldArgs(field) {
  return (field?.args || []).filter((a) => a?.name);
}

/** Input object fields for arg / input type expansion. */
export function getInputObjectFields(typeName, schemaIndex) {
  if (!typeName) return [];
  const typeDef = schemaIndex.get(typeName);
  if (!typeDef || typeDef.kind !== 'INPUT_OBJECT' || !typeDef.inputFields) return [];
  return typeDef.inputFields.filter((f) => f?.name);
}

export function isInputObjectType(type) {
  const inner = unwrapGraphQLType(type);
  return inner?.kind === 'INPUT_OBJECT' && !!inner.name;
}

export function isInputObjectExpandable(type, schemaIndex) {
  const inner = unwrapGraphQLType(type);
  if (inner?.kind !== 'INPUT_OBJECT' || !inner.name) return false;
  return getInputObjectFields(inner.name, schemaIndex).length > 0;
}

export function textMatchesFilter(text, filter) {
  if (!filter) return true;
  return String(text ?? '').toLowerCase().includes(filter);
}

export function inputObjectFieldMatchesFilter(field, filter) {
  if (!filter) return true;
  return textMatchesFilter(field.name, filter)
    || textMatchesFilter(formatGraphQLType(field.type), filter);
}

export function argMatchesFilter(arg, filter, schemaIndex) {
  if (!filter) return true;
  if (textMatchesFilter(arg.name, filter)) return true;
  if (textMatchesFilter(formatGraphQLType(arg.type), filter)) return true;
  const inner = unwrapGraphQLType(arg.type);
  if (inner?.kind === 'INPUT_OBJECT' && inner.name) {
    return getInputObjectFields(inner.name, schemaIndex).some((f) =>
      inputObjectFieldMatchesFilter(f, filter)
    );
  }
  return false;
}

/** True when a field name/type, its args, or any nested field matches the filter. */
export function fieldMatchesFilter(field, schemaIndex, filter, visitedTypes = new Set()) {
  if (!filter) return true;
  if (textMatchesFilter(field.name, filter)) return true;
  if (textMatchesFilter(formatGraphQLType(field.type), filter)) return true;

  if (getFieldArgs(field).some((arg) => argMatchesFilter(arg, filter, schemaIndex))) {
    return true;
  }

  const typeName = getFieldOutputTypeName(field);
  if (typeName && visitedTypes.has(typeName)) return false;

  const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;
  return getExplorerChildFields(field, schemaIndex).some((child) =>
    fieldMatchesFilter(child, schemaIndex, filter, nextVisited)
  );
}
